/* eslint-disable max-classes-per-file */
import shellapi from 'a365-shell-api';
import { Application } from 'app/application';
import { LoggerService } from 'app/logger/logger.service';
import { convertObjectToString } from 'app/shared/utils/string.util';
export { URI as Uri } from './uri';

export function notImplementedFunction(...args: unknown[]): never {
    throw new Error('Not implemented');
}

/**
 * Provides general interface for unmanaged resource clean-up.
 */
export interface IDisposable {
    /**
     * Cleans up the resource owned by this object.
     */
    dispose(): void;
}

export class Disposable {
    constructor(private readonly callOnDispose: () => void) {
    }

    public static from(...disposableLikes: Array<{ dispose: () => unknown }>): Disposable {
        return new Disposable(() => {
            disposableLikes.forEach((dl) => dl.dispose());
        });
    }

    public dispose(): void {
        this.callOnDispose();
    }
}

export class EventEmitter<T> {
    public static className = 'EventEmitter';
    private disposed = false;
    private listeners: Array<EventListener<T>> = [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public event: shellapi.Event<T> = (listener: (e: T) => any, thisArgs?: any, disposables?: shellapi.Disposable[]) => {
        if (this.disposed) {
            throw new Error('The event emitter is disposed');
        }

        if (thisArgs != null) {
            listener = listener.bind(thisArgs);
        }

        const result = new EventListener(listener);

        if (disposables != null) {
            disposables.push(result);
        }

        this.listeners.push(result);
        return result;
    };

    public fire(data?: T): void {
        if (this.disposed) {
            throw new Error('The event emitter is disposed');
        }

        this.listeners = this.listeners.filter((listener) => listener.callback != null);
        for (const listener of this.listeners) {
            if (listener.callback != null) {
                try {
                    listener.callback(data);
                } catch (error) {
                    const loggerService = Application.injector.get(LoggerService);
                    loggerService.error(EventEmitter.className, convertObjectToString(error) + ' when calling event handler');
                }
            }
        }
    }

    public dispose(): void {
        if (!this.disposed) {
            for (const listener of this.listeners) {
                listener.dispose();
            }
            this.listeners = null;
            this.disposed = true;
        }
    }
}

class EventListener<T> {
    constructor(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        public callback: (e: T) => any
    ) {
    }

    public dispose(): void {
        this.callback = null;
    }
}

let cachedPlatform: shellapi.A365ShellPlatform = null;
export function getPlatform(): shellapi.A365ShellPlatform {
    if (!cachedPlatform) {
        cachedPlatform = shellapi.A365ShellPlatform.Browser;
    }
    return cachedPlatform;
}

export enum A365ShellPlatform {
    Browser = 0,
    Electron = 1,
    ADS = 2
}

export enum AzureResource {
    Management = 'https://management.core.windows.net',
    ResourceManagement = 'https://management.azure.com/',
    Storage = 'https://storage.azure.com/',
    DataLake = 'https://datalake.azure.net/',
    KeyVault = 'https://vault.azure.net',
    SQL = 'https://database.windows.net/'
}

export enum AzureResourceMooncake {
    Storage = 'https://storage.azure.cn/'
}
